import { SettingsKeys } from '../../api/models/TranslationKeys';
import { CmsProperty } from '../../api/models/Shared.types';
import { Theme } from '../../config/theme';
import { CmsTabbedRibbon } from '../../components/cms/CmsTabbedRibbon';
import { EpgApi } from '../../api/EpgApi';
import { EpgUtil } from '../utility/EpgUtil';
import { EpgEpisodeRibbonDataSource } from './EpgEpisodeRibbonDataSource';
import { Backend } from '../../api/Backend';
import {
  IChannelV5,
  IProgramSummary,
  IScheduleDetail,
  ISeasonSummary,
  ISeriesDetail,
} from '../../api/models/AndromeTV.types';
import { CardSizeInfo } from './CardSizeInfo';
import { EpisodeRibbonDataSource } from './EpisodeRibbonDataSource';
import { RecordingRibbonDataSource } from './RecordingRibbonDataSource';

export interface TabbedDs {
  title: string;
  dataSource: EpgEpisodeRibbonDataSource | EpisodeRibbonDataSource | RecordingRibbonDataSource;
}

/**
 * @typedef {Object} EpgEpiRibbonData
 * @property {ISeriesDetail} series
 * @property {ISeasonSummary[]} seasons
 * @property {IProgramSummary[]} programs
 * @property {IScheduleDetail[]} schedules
 */
export interface EpgEpiRibbonData {
  series?: ISeriesDetail;
  seasons?: ISeasonSummary[];
  programs?: IProgramSummary[];
  schedules?: IScheduleDetail[];
}

/**
 * @property {Backend} backend
 * @property {DataSource[]} dataSources
 */
export class TabbedEpgEpisodesRibbonDataSource {
  private backend: Backend;
  private seriesId: string;
  private title: string;
  private seasonRibbons: TabbedDs[] = [];
  private buttonHeight: number;
  private channels: IChannelV5[];
  private defaultSeasonId: string | undefined;
  private fetchedData: boolean;
  series: ISeriesDetail | undefined;
  seasons: ISeasonSummary[] | undefined;
  programs: IProgramSummary[] | undefined;
  schedules: IScheduleDetail[] | undefined;
  private startIndex = -1;
  constructor(
    backend: Backend,
    seriesId: string,
    title: string,
    channels: IChannelV5[],
    defaultSeasonId: string | undefined = undefined,
    buttonHeight = 58
  ) {
    this.backend = backend;
    this.seriesId = seriesId;
    this.title = title;
    this.buttonHeight = buttonHeight;
    this.channels = channels;
    this.defaultSeasonId = defaultSeasonId;

    this.fetchedData = false;
    // this.sizeInfo = undefined;
  }

  static fromData(
    backend: Backend,
    seriesId: string,
    title: string,
    channels: IChannelV5[],
    data: EpgEpiRibbonData,
    defaultSeasonId: string | undefined = undefined,
    buttonHeight = 58
  ) {
    const dataSource = new TabbedEpgEpisodesRibbonDataSource(
      backend,
      seriesId,
      title,
      channels,
      defaultSeasonId,
      buttonHeight
    );
    dataSource.series = data.series;
    dataSource.schedules = data.schedules;
    dataSource.programs = data.programs;
    dataSource.seasons = data.seasons;
    dataSource.defaultSeasonId = defaultSeasonId;

    dataSource.fetchedData = true;
    return dataSource;
  }

  load(): Promise<TabbedDs[]> {
    if (this.fetchedData) {
      return Promise.resolve(this._createRibbons());
    } else {
      const fromUntil = EpgUtil.epgFromUntil(this.backend);
      return EpgApi.series(
        this.backend,
        this.seriesId,
        fromUntil.from,
        fromUntil.until,
        this.backend.deviceProfile!.profile.catalog
      ).then(result => {
        this.fetchedData = true;
        this.series = result.series;
        this.schedules = result.schedules;
        this.programs = result.programs;
        this.seasons = result.seasons;
        return this._createRibbons();
      });
    }
  }

  _createRibbons(): Promise<TabbedDs[]> {
    const sortedSeasons = EpgUtil.sortProgramsPerSeason(
      this.backend,
      this.seasons,
      this.programs,
      this.schedules,
      this.channels,
      this.series
    );
    let properties = this.backend.globalSettings.stringArray(
      SettingsKeys.propertiesEpgDetailEpisodesRibbon
    ) as CmsProperty[];
    properties = properties
      ? properties
      : [
          CmsProperty.ShowProgress,
          CmsProperty.UseBestSchedule,
          CmsProperty.ShowScheduleInfo,
          CmsProperty.ShowEpisodeInfo,
        ];
    let startIndex = 0;
    this.seasonRibbons = sortedSeasons.map((season, index) => {
      if (season.seasonId === this.defaultSeasonId) startIndex = index;
      return {
        dataSource: new EpgEpisodeRibbonDataSource(this.backend, season, this.channels, properties),
        title: season.title,
      };
    });
    if (this.seasonRibbons[startIndex]) {
      // have to load the first tab ribbon to get the correct size info
      return this.seasonRibbons[startIndex]!.dataSource.loadNext().then(() => {
        this.seasonRibbons[startIndex]!.dataSource.loadedIdx = 0;
        this.startIndex = startIndex;
        return this.seasonRibbons;
      });
    }
    return new Promise(() => {
      return this.seasonRibbons;
    });
  }

  get sizeInfo(): CardSizeInfo | undefined {
    return this.seasonRibbons[this.startIndex]?.dataSource.sizeInfo;
  }

  isEmpty(): boolean {
    return !this.seasonRibbons || this.seasonRibbons.length === 0;
  }

  noData(): boolean {
    return !this.seasonRibbons || this.seasonRibbons.length === 0;
  }

  totalHeight(): number {
    return Theme.Fonts.Ribbon.Title.lineHeight + this.buttonHeight + (this.sizeInfo ? this.sizeInfo.height : 0);
  }

  componentDescription() {
    return {
      focusable: true,
      type: CmsTabbedRibbon,
      defaultTab: this.startIndex,
      data: this,
      h: this.totalHeight(),
    };
  }
}
